import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import { UpdateUnitDto } from '../types';
import UnitForm from '../components/forms/UnitForm';
import { ArrowLeft } from 'lucide-react';

const EditUnitPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const unitId = parseInt(id!);

	const {
		data: unitResponse,
		isLoading: unitLoading,
		error: unitError
	} = useQuery({
		queryKey: ['unit', unitId],
		queryFn: () => apiService.getUnitById(unitId),
		enabled: !!unitId
	});

	const updateUnitMutation = useMutation({
		mutationFn: async ({ id, data }: { id: number; data: UpdateUnitDto }) => {
			const result = await apiService.updateUnit(id, data);
			return result;
		},
		onSuccess: (response) => {
			if (response.success && response.data) {
				queryClient.invalidateQueries({ queryKey: ['units'] });
				toast.success(`Unit "${response.data.unitNumber}" updated successfully!`);
				navigate(`/units/${unitId}`);
			} else {
				toast.error(`Error: ${response.message || 'Unknown error occurred'}`);
			}
		},
		onError: (error: any) => {
			console.error('Error updating unit:', error);
			const errorMessage = error?.response?.data?.message ||
				error?.response?.data?.errors?.join(', ') ||
				error.message ||
				'Unknown error';
			toast.error(`Error updating unit: ${errorMessage}`);
		}
	});

	const handleSubmit = (data: UpdateUnitDto) => {
		updateUnitMutation.mutate({ id: unitId, data });
	};

	if (unitLoading) {
		return (
			<div className="p-4">
				<div className="animate-pulse">
					<div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
					<div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
				</div>
			</div>
		);
	}

	if (unitError || !unitResponse?.data) {
		return (
			<div className="p-4">
				<div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
					<p className="text-red-800 dark:text-red-200">Error loading unit. Please try again.</p>
				</div>
			</div>
		);
	}

	const unit = unitResponse.data;

	return (
		<div className="p-4 space-y-6">
			{/* Header */}
			<div className="mb-2">
				<div className="flex items-center gap-2 mb-2">
					<div>
						<h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit Unit</h1>
						<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
							{unit.unitNumber} • {unit.clientLocationName}
						</p>
					</div>
				</div>
				{/* Back to Unit Link */}
				<button
					onClick={() => navigate(`/units/${unitId}`)}
					className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
				>
					<ArrowLeft className="w-4 h-4" />
					<span>Back to Unit</span>
				</button>
			</div>

			<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
				<UnitForm
					unit={unitResponse.data}
					onSubmit={handleSubmit}
					isLoading={updateUnitMutation.isPending}
					error={updateUnitMutation.error ? 'Failed to update unit' : undefined}
				/>
			</div>
		</div>
	);
};

export default EditUnitPage;

